import { Link } from "react-router-dom";
import { CopyrightNotice } from "@/components/legal/CopyrightNotice";
import { ShieldAlert, Zap, Truck, Scale, TrendingUp, Mail } from "lucide-react";

export default function Legal() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="legal-page">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 mb-6">
        <ShieldAlert className="h-3.5 w-3.5 text-[#FF9F0A]" />
        <span className="text-xs font-semibold text-[#FF9F0A] uppercase tracking-widest">Legal Notice</span>
      </div>
      <h1 className="font-black tracking-tight text-4xl sm:text-5xl">
        The honest fine print<span className="text-[#007AFF]">.</span>
      </h1>
      <p className="mt-4 text-neutral-400 leading-relaxed">
        We wrote this in plain English so there is no confusion about what Vismarks does — and what it
        does not promise. By using Vismarks, you agree to everything on this page and to the full documents in the{" "}
        <Link to="/legal-center" className="text-[#007AFF] hover:underline" data-testid="legal-center-link">Legal Center</Link>{" "}
        (Terms of Service, Seller Agreement, Stage &amp; Creator Terms, Refund Policy, Community Guidelines, IP Policy, Privacy and Cookie Policy).
      </p>
      <CopyrightNotice className="mt-6" />
      <p className="mt-3 text-xs text-neutral-500" data-testid="imprint-founder">Founded in 2026 by Ayanfe Mary-Grace Adesipe. Operating entity: VISMARKS NIGERIA LIMITED (incorporation in progress).</p>

      <div className="mt-12 space-y-10">
        <Section icon={Zap} title="1. Vismarks is a visibility engine — not a sales guarantee">
          <p>
            Vismarks gives your products powerful visibility: every listing is shown to matched buyers
            with always-on visibility — it stays live until it sells out or you remove it, boosted by AI
            recommendations. That is what we promise, and that is
            <span className="text-white font-semibold"> all</span> we promise.
          </p>
          <p className="mt-3">
            <span className="text-white font-semibold">We do not guarantee that any product will sell.</span>{" "}
            References to "1 million products a day", "sold fast", or similar phrases describe
            the platform's capacity and design goal — always-on visibility for every listing — not a promise of
            sales volume, revenue, or results for any individual seller.
          </p>
        </Section>

        <Section icon={TrendingUp} title="2. Your results depend on many things we don't control">
          <p>
            Whether a product sells depends on buyer demand, your pricing, product quality, photos,
            descriptions, competition, seasonality, and overall traffic on the platform. Two sellers
            with the same subscription can have completely different outcomes. Any figures shown on
            Vismarks (views, projections, platform stats) are informational only and are
            <span className="text-white font-semibold"> not a forecast of your future sales</span>.
          </p>
        </Section>

        <Section icon={Truck} title="3. Sellers handle all logistics">
          <p>
            Vismarks is not a logistics provider, fulfilment network, or courier. Sellers are solely
            responsible for packing, shipping, delivery timelines, and returns of every product they
            publish. Buyers arrange delivery directly with the seller.
          </p>
        </Section>

        <Section icon={Scale} title="4. Fees, subscriptions, and refunds">
          <p>
            Sign-up is free and your first 5 listings are free, display-only. Selling requires the Seller Plan ($100/month or
            $1,000/year) plus a platform fee of 0.5% of each item sold, deducted at settlement. One Stage Hosting kind and your
            first live show are free; extra Hosting kinds are $20/month. Tips, tickets and memberships carry a 0.5% platform fee.
            Subscription fees pay for visibility and platform access — they are{" "}
            <span className="text-white font-semibold">not contingent on making sales</span> and are not refundable because a
            product did not sell. Full details: <Link to="/seller-agreement" className="text-[#007AFF] hover:underline">Seller Agreement</Link>,{" "}
            <Link to="/stage-terms" className="text-[#007AFF] hover:underline">Stage &amp; Creator Terms</Link> and{" "}
            <Link to="/refund-policy" className="text-[#007AFF] hover:underline">Refund Policy</Link>.
          </p>
        </Section>

        <Section icon={ShieldAlert} title="5. Limitation of liability (plain English)">
          <p>
            To the maximum extent permitted by law, Vismarks is not liable for lost profits, lost
            revenue, or missed sales expectations. Our total liability to you for any claim is limited
            to the amount you paid us in subscription fees in the 3 months before the claim arose.
            Nothing here removes rights that your local consumer law says cannot be waived.
          </p>
        </Section>

        <Section icon={Truck} title="6. Shipping policy">
          <p>
            Sellers dispatch orders within <span className="text-white font-semibold">48 hours</span> of
            payment confirmation. Typical delivery is{" "}
            <span className="text-white font-semibold">3–7 business days</span> depending on the buyer's
            location and the delivery method the seller and buyer agree on. Buyer payments are protected
            until the buyer confirms delivery. If an order is not dispatched within the 48-hour window or
            is not delivered, the buyer is entitled to a refund through the dispute process.
          </p>
        </Section>

        <Section icon={ShieldAlert} title="7. Product policy & external feeds (Google Shopping and others)">
          <p>
            VISMARKS distributes eligible listings to external channels such as Google Shopping to
            maximise visibility. To protect the platform's standing, listings must comply with the
            policies of those channels. We automatically exclude from external feeds — and may remove
            from the platform — listings that are:
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>services, accommodation, bookings, real estate, or other non-physical offerings;</li>
            <li>priced with placeholder or misleading amounts (e.g. $0.01);</li>
            <li>counterfeit, regulated, dangerous, or otherwise prohibited by law or channel policy;</li>
            <li>misrepresented in title, images, description, or availability.</li>
          </ul>
          <p className="mt-3">
            Exclusion from external feeds does not remove a compliant listing from VISMARKS itself.
            Repeated or serious violations may lead to listing removal, seller strikes, or account
            suspension under our trust &amp; safety rules.
          </p>
        </Section>

        <Section icon={Mail} title="8. Questions">
          <p>
            If anything here is unclear, contact us before subscribing or publishing. We would rather
            answer a question up front than have a disappointed seller later.
          </p>
        </Section>
      </div>

      <div className="mt-14 rounded-xl border border-white/10 bg-white/[0.03] p-6">
        <div className="font-bold text-white">The one-line version</div>
        <p className="text-sm text-neutral-400 mt-2 leading-relaxed">
          Vismarks gets your products seen by the right buyers, fast. Selling them still depends on
          your product, your price, and the market. No one — including us — can guarantee sales.
        </p>
        <Link to="/register" className="inline-block mt-4 text-sm text-[#007AFF] hover:underline" data-testid="legal-register-link">
          Sounds fair? Create your account →
        </Link>
      </div>

      <div className="mt-12 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="imprint-section">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-[#007AFF]" />
          <h2 className="font-bold text-lg text-white">Provider identification (imprint)</h2>
        </div>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Operator</dt>
            <dd className="mt-0.5 text-white">VISMARKS — company details to be updated</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Registered address</dt>
            <dd className="mt-0.5 text-neutral-300">Registration in progress — to be published here</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Legal contact</dt>
            <dd className="mt-0.5 text-neutral-300">legal@vismarks.com</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Privacy contact</dt>
            <dd className="mt-0.5 text-neutral-300">privacy@vismarks.com</dd>
          </div>
        </dl>
      </div>

      <p className="mt-10 text-[11px] text-neutral-500">Last updated: June 2026</p>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-3">
        <span className="h-8 w-8 rounded-md bg-[#007AFF]/10 border border-[#007AFF]/30 inline-flex items-center justify-center flex-shrink-0">
          <Icon className="h-4 w-4 text-[#007AFF]" />
        </span>
        <h2 className="font-bold text-lg text-white">{title}</h2>
      </div>
      <div className="mt-3 text-sm text-neutral-400 leading-relaxed pl-11">{children}</div>
    </section>
  );
}
